import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  SelectLocationProperties,
  SelectLocationSources,
} from "@/analytics/generic-events-properties";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { PathWaypointTarget } from "@/components/r3f/renderers/odometry-paths/odometry-path-renderer";
import { encodeCameraParameters } from "@/components/r3f/utils/camera-parameters";
import {
  useCameraRotationFor2dOverviewAndMiniMap,
  useCenterCameraOnPlaceholders,
} from "@/hooks/use-center-camera-on-placeholders";
import { obbToPlanes } from "@/hooks/use-clipping-planes";
import { useDetectTooManyRedraws } from "@/hooks/use-detect-too-many-redraws";
import { useCurrentAreaClippingBox } from "@/hooks/use-object-bounding-box";
import { IElementWaypoint, isIElementWaypoint } from "@/hooks/use-placeholders";
import { ModeSceneProps } from "@/modes/mode";
import {
  useCurrentAreaIfAvailable,
  useCurrentScene,
  useIsCurrentAreaLoading,
} from "@/modes/mode-data-context";
import { selectIsElementViewablePointCloudStream } from "@/modes/mode-selectors";
import {
  useCached3DObjectsIfExists,
  useValidateCacheSize,
} from "@/object-cache";
import { selectMeasurements } from "@/store/measurement-tool-selector";
import { changeMode } from "@/store/mode-slice";
import { setFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { PickingTools } from "@/tools/picking-tools";
import { usePickingToolsCallbacks } from "@/tools/use-picking-tools-callbacks";
import {
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
} from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { isIElementViewableGaussianSplats } from "@/types/type-guards";
import { CameraViewType } from "@/utils/cam-view";
import { getCameraAnimationTime } from "@/utils/camera-animation-time";
import { atEyeLevel } from "@/utils/camera-transform";
import { IS_DEV } from "@/utils/env-constants";
import {
  OrthoFrustum,
  selectAncestor,
  selectChildDepthFirst,
  selectIElementWorldPosition,
  useNonExhaustiveEffect,
  useReproportionCamera,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementImg360,
  IElementType,
  isIElementImg2d,
  isIElementImg360,
  isIElementImgSheetTiled,
  isIElementModel3d,
  isIElementRoomSection,
  isIElementSectionDataSetCapture,
  type IElementGenericImgSheet,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { DEFAULT_PERSPECTIVE_FOV, reproportionCamera } from "@faro-lotv/lotv";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Box3, Camera, OrthographicCamera, Quaternion, Vector3 } from "three";
import type { WalkSceneActiveElement } from "../walk-mode/walk-types";
import { useSceneContextMenu } from "./scene-context-menu/use-scene-context-menu";
import { NewWaypointsUI } from "./sheet-context-menu/new-waypoints/new-waypoints-ui";
import { selectAllPanoramasFromPaths } from "./sheet-mode-selectors";
import { SheetSceneBase, SheetSceneBaseApi } from "./sheet-scene-base";
import { SheetModeInitialState } from "./sheet-state";

/** Camera planes used when entering walk mode on a point of a trajectory */
const WALK_CAMERA_NEAR = 0.1;
const WALK_CAMERA_FAR = 1000;

/**
 * Setup an orthographic camera to frame the list of input panos
 *
 * @param camera The camera to setup
 * @param panos The list of panos to frame
 * @param initialState Optional initial state of the mode
 */
function useCenterCameraOnLookAtPano(
  camera: Camera,
  panos: IElementImg360[],
  initialState?: SheetModeInitialState,
): void {
  assert(
    camera instanceof OrthographicCamera,
    "Sheet mode camera should be orthographic",
  );

  const position = useAppSelector(
    selectIElementWorldPosition(initialState?.lookAtId),
  );
  const { size } = useThree();

  if (!initialState || !initialState.lookAtId) return;

  const lookAtPano = panos.find((p) => p.id === initialState.lookAtId);
  if (!lookAtPano) return;

  camera.position.x = position[0];
  camera.position.z = position[2];
  camera.updateMatrix();
  camera.right = 3;
  camera.left = -3;
  camera.top = 3;
  camera.bottom = -3;
  reproportionCamera(camera, size.width / size.height);
}

/** Value used to increase the size of the bounding box in order to add some padding while calculating zoom */
const ZOOM_PADDING_FACTOR = 1.1;

type CameraTargetData = {
  /** Target position to which the camera should be moved */
  targetPosition: Vector3;

  /** Target quaternion of the camera */
  targetQuaternion?: Quaternion;

  /** Target frustum of the camera */
  frustum?: OrthoFrustum;

  /** Desired zoom value of the camera */
  zoom: number;
};

/**
 * @returns The main Scene for the Sheet Mode
 */
export function SheetScene({
  initialState,
}: ModeSceneProps<SheetModeInitialState>): JSX.Element {
  useDetectTooManyRedraws("SheetScene");

  // Get the relevant data for the mode from the store
  const isLoading = useIsCurrentAreaLoading();
  const {
    activeSheets,
    availableSheets,
    activeSheetFor2DNavigation,
    panos,
    photos,
    orthomosaics,
    paths,
    referenceElement,
    annotations,
    locationMarkers,
  } = useCurrentScene();

  // Get active pano and sheet from app store
  const dispatch = useAppDispatch();

  const sheetSceneBaseRef = useRef<SheetSceneBaseApi>(null);

  const showImageInWalkMode = useCallback(
    (target: WalkSceneActiveElement) => {
      dispatch(setActiveElement(target.id));
      dispatch(setFlyWalkSceneFilter(SceneFilter.Pano));
      dispatch(changeMode("walk"));
    },
    [dispatch],
  );

  const camera = useThree((s) => s.camera);
  useReproportionCamera(camera);

  useCenterCameraOnLookAtPano(camera, panos, initialState);

  // Take into account the trajectories when centering the camera
  const pathPanos = useAppSelector(selectAllPanoramasFromPaths(paths));

  const allPanos = useMemo(() => [...panos, ...pathPanos], [panos, pathPanos]);

  const areaBox = useCurrentAreaClippingBox();
  const cameraRotation = useCameraRotationFor2dOverviewAndMiniMap();
  const cameraData = useCenterCameraOnPlaceholders({
    areaVolume: areaBox,
    sheetElements: activeSheets,
    cameraRotation,
    placeholders: allPanos,
  });

  // The sheet mode currently only clips using an area's volume, since the user has no way to edit a custom one.
  const clippingPlanes = useMemo(
    () => (areaBox ? obbToPlanes(areaBox) : undefined),
    [areaBox],
  );

  // re-center camera if the world transform of the current area changes
  const currentArea = useCurrentAreaIfAvailable();
  const [cameraTargetData, setCameraTargetData] = useState<CameraTargetData>();

  // This value only changes when the camera rotation components have changed
  const newCameraRotationValue = useMemo(
    () => cameraData.quaternion,
    // Only update, when the components change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cameraData.quaternion.x,
      cameraData.quaternion.y,
      cameraData.quaternion.z,
      cameraData.quaternion.w,
    ],
  );

  // This effect needs to run before the effect below,
  // because the setCameraTargetData from the centering logic should have priority
  useNonExhaustiveEffect(() => {
    if (camera instanceof OrthographicCamera) {
      setCameraTargetData({
        // We are keeping the camera at the same zoom and position but changing the quaternion
        targetPosition: camera.getWorldPosition(new Vector3()),
        targetQuaternion: newCameraRotationValue,
        zoom: camera.zoom,
      });
    }
    // We don't want to rerun this useEffect when there are some changes in the camera
  }, [newCameraRotationValue]);

  // UseLayoutEffects are re-triggered every time the parent suspense renders the fallback component, so
  // it's much safer to wrap the re-centering logic in a useEffect
  useEffect(() => {
    if (camera instanceof OrthographicCamera && !initialState && !isLoading) {
      setCameraTargetData({
        targetPosition: cameraData.position,
        targetQuaternion: cameraData.quaternion,
        zoom: camera.zoom,
        frustum: cameraData.frustum,
      });
      // As we assign the frustum we need to put manual to true
      Object.assign(camera, { manual: true });
    }
    // We want to center every-time the current active sheet changes or the loading finishes
    // This hook depends on area.id and not on sheet, so that when changing the active sheet
    // the camera does not move
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, isLoading, currentArea?.area?.id]);

  const frameBox = useCallback(
    (boundingBox: Box3) => {
      if (!(camera instanceof OrthographicCamera)) {
        throw Error("Expected orthographic camera");
      }
      const targetPosition = boundingBox.getCenter(new Vector3());
      // Keeping the height of the camera as is so that it doesn't move too close to path
      targetPosition.y = camera.position.y;

      // Convert the bounding box co-ordinates into camera space
      const pathBboxInCameraSpace = boundingBox
        .clone()
        .applyMatrix4(camera.matrixWorldInverse);

      const boundingBoxWidth =
        pathBboxInCameraSpace.max.x - pathBboxInCameraSpace.min.x;
      const boundingBoxHeight =
        pathBboxInCameraSpace.max.y - pathBboxInCameraSpace.min.y;

      const frustumWidth = camera.right - camera.left;
      const frustumHeight = camera.top - camera.bottom;

      const widthScale =
        frustumWidth / (boundingBoxWidth * ZOOM_PADDING_FACTOR);
      const heightScale =
        frustumHeight / (boundingBoxHeight * ZOOM_PADDING_FACTOR);

      setCameraTargetData({
        targetPosition,
        zoom: Math.min(widthScale, heightScale),
      });
    },
    [camera],
  );

  const shouldAnimateCameraToFitObject = useCallback(
    (
      ev: ThreeEvent<MouseEvent>,
      path: IElementGenericOdometryPath,
      boundingBox: Box3,
    ) => {
      dispatch(setActiveElement(path.id));
      frameBox(boundingBox);
    },
    [dispatch, frameBox],
  );

  const mapAnnotations = useMemo(
    () =>
      annotations.filter(
        (a) =>
          !isIElementModel3d(a) &&
          !isIElementImg2d(a) &&
          !a.metaDataMap?.isEstimatedFrom2DPano,
      ),
    [annotations],
  );

  // Display all measurements across all available sheets, not only those on the currently active sheet
  const availableSheetsIds = useMemo(
    () => availableSheets.map((s) => s.id),
    [availableSheets],
  );
  const measurements = useAppSelector(
    selectMeasurements(availableSheetsIds),
    isEqual,
  );

  const { lookAt, setCameraView } = useSceneEvents();
  useTypedEvent(lookAt, (position: Vector3) => {
    // Frame a box of 10m x 10m around the position to look at
    frameBox(new Box3().expandByPoint(position).expandByScalar(10));
  });
  useTypedEvent(setCameraView, (cameraView) => {
    if (
      cameraView.type === CameraViewType.orthographic &&
      camera instanceof OrthographicCamera
    ) {
      const frustumHeight = camera.top - camera.bottom;

      setCameraTargetData({
        targetPosition: cameraView.position,
        targetQuaternion: cameraView.rotation,
        zoom: frustumHeight / cameraView.zoomLevel,
      });
    }
  });

  // Prevents the CameraAnimation from restarting due to a change in dependencies
  const clearCameraTargetData = useCallback(() => {
    setCameraTargetData(undefined);
  }, []);

  const sheetObjects = useCached3DObjectsIfExists(activeSheets, "SheetScene");
  const [isPickingToolEnabled, setIsPickingToolEnabled] = useState(false);
  const pickingToolsCallbacks = usePickingToolsCallbacks();

  const activeTool = useAppSelector(selectActiveTool);
  const isWaypointMoveActive = activeTool === ToolName.editWaypoints;

  const { getState } = useAppStore();

  const onPlaceholderClicked = useCallback(
    (target: IElementWaypoint) => {
      Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
        via: SelectLocationSources.sheetView,
        tool: computeToolPropertyForSelectLocationEvent(activeTool),
      });

      if (isIElementImg2d(target) || isIElementImg360(target)) {
        showImageInWalkMode(target);
      }

      if (isIElementPhotogrammetryScan(target)) {
        const orthoMosaic = selectChildDepthFirst(
          target,
          isIElementOrthomosaic,
        )(getState());
        assert(
          orthoMosaic,
          "Expected to find ortho mosaic in photogrammetry scan",
        );
        showImageInWalkMode(orthoMosaic);
      }

      if (isIElementRoomSection(target)) {
        dispatch(setActiveElement(target.id));
      }
    },
    [activeTool, dispatch, getState, showImageInWalkMode],
  );

  // Hide waypoint preview when opening the context menu to avoid visual clutter, but still allow the user to explore the scene
  // after it is closed.
  const hideWaypointPreview = useCallback((target: IElement) => {
    if (isIElementWaypoint(target)) {
      sheetSceneBaseRef.current?.hideWaypointPreview();
    }
  }, []);

  const { sceneContextMenuComponent, onSceneContextMenu } = useSceneContextMenu(
    { onContextMenuShown: hideWaypointPreview },
  );

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  // Trajectories described by an odometry file have no panorama to open, so the user walks
  // the 3d reconstruction of the same capture instead. Models attached to the area are ignored.
  const onTrajectoryPointClicked = useCallback(
    ({ path, position, walkPosition, direction }: PathWaypointTarget) => {
      const state = getState();

      const dataSetCapture = selectAncestor(
        path,
        isIElementSectionDataSetCapture,
      )(state);
      if (!dataSetCapture) return;

      const gaussianSplats = selectChildDepthFirst(
        dataSetCapture,
        isIElementViewableGaussianSplats,
      )(state);
      const model =
        gaussianSplats ??
        selectChildDepthFirst(dataSetCapture, (el) =>
          selectIsElementViewablePointCloudStream(el)(state),
        )(state);
      if (!model) return;

      const sceneFilter = gaussianSplats
        ? SceneFilter.GaussianSplats
        : SceneFilter.PointCloud;
      // The trajectory point is already flattened to floor level, so raise it to eye height
      const eye = atEyeLevel(state.iElements, path, walkPosition ?? position);
      const target = eye.clone().add(direction ?? new Vector3(0, 0, -1));

      dispatch(setActiveElement(model.id));
      dispatch(setFlyWalkSceneFilter(sceneFilter));
      dispatch(
        changeMode({
          mode: "walk",
          initialState: {
            scene: sceneFilter,
            camera: encodeCameraParameters({
              near: WALK_CAMERA_NEAR,
              far: WALK_CAMERA_FAR,
              fov: DEFAULT_PERSPECTIVE_FOV,
              pos: eye.toArray(),
              target: target.toArray(),
            }),
          },
        }),
      );
    },
    [dispatch, getState],
  );

  return (
    <>
      <SheetSceneBase
        ref={sheetSceneBaseRef}
        sheets={sheetObjects}
        sheetElementForElevation={activeSheetFor2DNavigation}
        referenceElement={referenceElement}
        paths={paths}
        panos={panos}
        photos={photos}
        orthomosaics={orthomosaics}
        locationMarkers={locationMarkers}
        annotations={mapAnnotations}
        measurements={measurements}
        clippingPlanes={clippingPlanes}
        transparentSheet
        onPlaceholderClicked={onPlaceholderClicked}
        onTrajectoryPointClicked={onTrajectoryPointClicked}
        onSceneContextMenu={hasWritePermission ? onSceneContextMenu : undefined}
        onPathActivated={shouldAnimateCameraToFitObject}
        pickingToolsCallbacks={
          isPickingToolEnabled ? pickingToolsCallbacks : undefined
        }
        isWaypointMoveActive={isWaypointMoveActive}
      />

      <PickingTools
        ref={pickingToolsCallbacks.tools}
        activeModels={sheetObjects}
        onActiveToolChanged={(activeToolAndPicking) =>
          setIsPickingToolEnabled(activeToolAndPicking.isPickingToolActive)
        }
      />

      {cameraTargetData && (
        <CameraAnimation
          position={cameraTargetData.targetPosition}
          quaternion={cameraTargetData.targetQuaternion}
          frustum={cameraTargetData.frustum}
          onAnimationFinished={clearCameraTargetData}
          zoom={cameraTargetData.zoom}
          duration={getCameraAnimationTime(
            camera,
            cameraTargetData.targetPosition,
            { min: 0.5, max: 1 },
          )}
        />
      )}

      <NewWaypointsUI />

      {sceneContextMenuComponent}

      {IS_DEV && <ObjectCacheValidator activeSheets={activeSheets} />}
    </>
  );
}

type ObjectCacheValidatorProps = {
  /** The active sheets */
  activeSheets: IElementGenericImgSheet[];
};

/**
 * This component validates that, in sheet mode, no point clouds, panos, 2d photos, or gaussian splats
 * are acquired in the object cache. If this is not the case, a detailed console.warn is raised.
 * A maximum of one CAD model acquired is allowed, because CadModelTreeData may be using it.
 *
 * @param maxSheets The maximum number of sheets allowed in the object cache.
 * @returns null
 */
function ObjectCacheValidator({
  activeSheets,
}: ObjectCacheValidatorProps): null {
  const numTiledSheets = activeSheets.reduce(
    (count, sheet) => count + (isIElementImgSheetTiled(sheet) ? 1 : 0),
    0,
  );

  useValidateCacheSize(
    IElementType.imgSheet,
    activeSheets.length - numTiledSheets,
  );
  useValidateCacheSize(IElementType.imgSheetTiled, numTiledSheets);
  useValidateCacheSize(IElementType.pointCloudStream, 0);
  useValidateCacheSize(IElementType.pointCloudStreamWebShare, 0);
  useValidateCacheSize(IElementType.model3dStream, 1);
  useValidateCacheSize(IElementType.img360, 0);
  useValidateCacheSize(IElementType.img2d, 0);
  useValidateCacheSize(IElementType.gaussianSplats, 0);
  return null;
}
